import React, { useState, useEffect, useRef } from 'react';
import ReactDOM from 'react-dom';

/**
 * A reusable multi-select combobox component.
 * Allows searching and selecting multiple items from a list.
 * Selected items are displayed as chips.
 */
const MultiSelectCombobox = ({
    items,
    selectedItems = [], // Array of IDs
    onChange, // Returns new array of selected IDs
    placeholder = 'Ara ve seç...',
    className,
    labelKey = 'name',
    valueKey = 'id'
}) => {
    const [isOpen, setIsOpen] = useState(false);
    const [query, setQuery] = useState('');
    const [coords, setCoords] = useState({ top: 0, left: 0, width: 0 });
    const containerRef = useRef(null);

    // Filter available items (exclude already selected ones)
    // Also filter by query
    const filteredItems = items.filter(item => {
        const isSelected = selectedItems.includes(item[valueKey]);
        if (isSelected) return false;

        if (!query) return true;
        return item[labelKey].toString().toLowerCase().includes(query.toLowerCase());
    });

    const updateCoords = () => {
        if (containerRef.current) {
            const rect = containerRef.current.getBoundingClientRect();
            setCoords({
                top: rect.bottom + window.scrollY,
                left: rect.left + window.scrollX,
                width: rect.width
            });
        }
    };

    const handleFocus = () => {
        updateCoords();
        setIsOpen(true);
    };

    const handleSelect = (item) => {
        const newSelected = [...selectedItems, item[valueKey]];
        onChange(newSelected);
        setQuery('');
        // Keep open for multiple selection or close? 
        // Typically keep open or focus back input
        // Let's keep input focused
    };

    const handleRemove = (idToRemove) => {
        const newSelected = selectedItems.filter(id => id !== idToRemove);
        onChange(newSelected);
    };

    // Click outside handler
    useEffect(() => {
        const handleGlobalClick = (e) => {
            if (containerRef.current && containerRef.current.contains(e.target)) return;
            if (e.target.closest('.multi-combobox-portal-dropdown')) return;
            setIsOpen(false);
        };

        if (isOpen) {
            document.addEventListener('mousedown', handleGlobalClick);
            window.addEventListener('resize', updateCoords);
            window.addEventListener('scroll', updateCoords, true);
        }

        return () => {
            document.removeEventListener('mousedown', handleGlobalClick);
            window.removeEventListener('resize', updateCoords);
            window.removeEventListener('scroll', updateCoords, true);
        };
    }, [isOpen]);

    // Get selected objects for display
    const selectedObjects = items.filter(item => selectedItems.includes(item[valueKey]));

    return (
        <div className={`multi-combobox-container ${className || ''}`} ref={containerRef} style={{ position: 'relative' }}>
            <div
                className="flex flex-wrap items-center gap-2 p-2 min-h-[46px] cursor-text"
                style={{
                    background: 'rgba(255, 255, 255, 0.02)',
                    backdropFilter: 'blur(8px)',
                    border: '1px solid rgba(255, 255, 255, 0.08)',
                    borderRadius: '14px',
                    boxShadow: 'inset 0 1px 1px rgba(255,255,255,0.05)',
                    transition: 'all 0.3s ease'
                }}
                onClick={() => document.getElementById(`input-${valueKey}`)?.focus()}
            >
                {selectedObjects.map(item => (
                    <div
                        key={item[valueKey]}
                        className="flex items-center gap-2 animate-fade-in"
                        style={{
                            background: 'rgba(59, 130, 246, 0.15)',
                            border: '1px solid rgba(59, 130, 246, 0.3)',
                            padding: '4px 10px',
                            borderRadius: '10px',
                            fontSize: '12px',
                            color: '#fff',
                            fontWeight: '500',
                            backdropFilter: 'blur(4px)'
                        }}
                    >
                        <span>{item[labelKey]}</span>
                        <button
                            onClick={(e) => { e.stopPropagation(); handleRemove(item[valueKey]); }}
                            style={{
                                border: 'none',
                                background: 'rgba(255,255,255,0.1)',
                                color: '#fff',
                                borderRadius: '50%',
                                width: '16px',
                                height: '16px',
                                display: 'flex',
                                alignItems: 'center',
                                justifyContent: 'center',
                                fontSize: '10px',
                                cursor: 'pointer',
                                transition: 'all 0.2s'
                            }}
                            onMouseOver={e => e.currentTarget.style.background = '#ef4444'}
                            onMouseOut={e => e.currentTarget.style.background = 'rgba(255,255,255,0.1)'}
                        >
                            ✕
                        </button>
                    </div>
                ))}
                <input
                    id={`input-${valueKey}`}
                    type="text"
                    className="flex-1 bg-transparent border-none outline-none text-white min-w-[120px] p-1"
                    style={{ fontSize: '13px' }}
                    value={query}
                    onChange={e => { setQuery(e.target.value); setIsOpen(true); }}
                    onFocus={handleFocus}
                    placeholder={selectedItems.length === 0 ? placeholder : ''}
                />
            </div>

            {isOpen && ReactDOM.createPortal(
                <div
                    className="multi-combobox-portal-dropdown custom-scrollbar"
                    style={{
                        position: 'absolute',
                        top: `${coords.top + 6}px`,
                        left: `${coords.left}px`,
                        width: `${coords.width}px`,
                        maxHeight: '240px',
                        overflowY: 'auto',
                        zIndex: 9999,
                        background: 'rgba(15, 23, 42, 0.95)',
                        backdropFilter: 'blur(20px)',
                        border: '1px solid rgba(255, 255, 255, 0.12)',
                        borderRadius: '16px',
                        boxShadow: '0 20px 25px -5px rgba(0, 0, 0, 0.5), 0 10px 10px -5px rgba(0, 0, 0, 0.4)',
                        padding: '6px'
                    }}
                >
                    {filteredItems.length > 0 ? (
                        filteredItems.map(item => (
                            <div
                                key={item[valueKey]}
                                onClick={() => handleSelect(item)}
                                className="p-3 cursor-pointer flex items-center justify-between text-sm transition-all duration-200"
                                style={{
                                    borderRadius: '10px',
                                    color: 'rgba(255, 255, 255, 0.8)',
                                    marginBottom: '2px'
                                }}
                                onMouseOver={e => {
                                    e.currentTarget.style.background = 'rgba(255, 255, 255, 0.05)';
                                    e.currentTarget.style.color = '#fff';
                                }}
                                onMouseOut={e => {
                                    e.currentTarget.style.background = 'transparent';
                                    e.currentTarget.style.color = 'rgba(255, 255, 255, 0.8)';
                                }}
                            >
                                <span style={{ fontWeight: '500' }}>{item[labelKey]}</span>
                                <span style={{
                                    fontSize: '10px',
                                    padding: '2px 8px',
                                    background: 'rgba(59, 130, 246, 0.1)',
                                    color: '#3b82f6',
                                    borderRadius: '6px',
                                    fontWeight: 'bold',
                                    textTransform: 'uppercase'
                                }}>Ekle</span>
                            </div>
                        ))
                    ) : (
                        <div className="p-4 text-center text-secondary text-xs" style={{ opacity: 0.6 }}>
                            {query ? 'Sonuç bulunamadı' : 'Tüm seçenekler seçildi'}
                        </div>
                    )}
                </div>,
                document.body
            )}
        </div>
    );
};

export default MultiSelectCombobox;
